import type { TrackList } from './model/track.ts';

export type SubtitleSlot = 'mainSubtitle' | 'secondSubtitle';

export type LanguagePreferenceList = string[];

/** MRU language list helpers — mirrors utils.js insertLanguage / removeLanguage. */
export function insertLanguage(languages: string[], language: string): string[] {
  const arr = languages.slice();
  const index = arr.indexOf(language);
  if (index !== -1) {
    arr.splice(index, 1);
  }
  arr.unshift(language);
  return arr;
}

export function removeLanguage(languages: string[], language: string): string[] {
  const arr = languages.slice();
  const index = arr.indexOf(language);
  if (index !== -1) {
    arr.splice(index, 1);
  }
  return arr;
}

export function languagesForSlot(
  slot: SubtitleSlot,
  mainLanguages: LanguagePreferenceList,
  secondLanguages: LanguagePreferenceList,
): LanguagePreferenceList {
  return slot === 'mainSubtitle' ? mainLanguages : secondLanguages;
}

export function shouldSkipPreferredSelection(languages: LanguagePreferenceList): boolean {
  return languages[0] === 'null';
}

// --- YouTube (B-SRC-YT-01/02, B-SEL-01) ---

export type YoutubeCaptionTrack = {
  name: { simpleText: string };
  languageCode?: string;
  kind?: string;
  enhanced?: boolean;
  /**
   * Creator text mapped onto ASR timing, then server enhance.
   * Must NOT set `enhanced: true` (restoreShare / Restored would collide).
   */
  creatorEnhanced?: boolean;
  whisper?: boolean;
  restored?: boolean;
  vssId?: string;
  downloadable?: boolean;
  baseUrl?: string;
  /** Paired ASR timedtext URL when `creatorEnhanced` is set. */
  asrBaseUrl?: string;
};

export type YoutubeServiceLabels = {
  whisperTitle: string;
  restoredSuffix: string;
  enhancedSuffix: string;
};

/** en ↔ en-US style compatibility (same rule as preferred-track pick). */
export function youtubeLanguagesCompatible(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.includes(b) || b.includes(a);
}

function isCreatorCaptionTrack(track: YoutubeCaptionTrack): boolean {
  return (
    track.kind !== 'asr'
    && !track.enhanced
    && !track.creatorEnhanced
    && !track.whisper
    && !track.restored
  );
}

/** Insert creator-enhanced clones (one per manual track).
 *  Same-language ASR → set `asrBaseUrl` (server map path).
 *  No compatible ASR → omit `asrBaseUrl` (server synth path).
 *  Display suffix reuses `enhancedSuffix` (same user-facing label as Enhanced).
 *  Insert before ASR Enhanced / Whisper so manual 斷句 stays ahead of
 *  auto-generated 斷句 (mirrors English before English auto-generated). */
export function injectYoutubeCreatorEnhancedTracks(
  captionTracks: YoutubeCaptionTrack[],
  labels: Pick<YoutubeServiceLabels, 'enhancedSuffix'>,
): YoutubeCaptionTrack[] {
  const arr = captionTracks.slice();
  const asrTracks = arr.filter(
    (track) =>
      track.kind === 'asr'
      && !!track.baseUrl
      && !track.enhanced
      && !track.whisper
      && !track.restored
      && !track.creatorEnhanced,
  );

  const injected: YoutubeCaptionTrack[] = [];
  for (let i = 0; i < arr.length; i += 1) {
    const track = arr[i];
    if (!track || !isCreatorCaptionTrack(track) || !track.baseUrl) continue;
    const asr = asrTracks.find((candidate) =>
      youtubeLanguagesCompatible(track.languageCode, candidate.languageCode),
    );

    const clone = JSON.parse(JSON.stringify(track)) as YoutubeCaptionTrack;
    clone.name.simpleText += ` (${labels.enhancedSuffix})`;
    clone.creatorEnhanced = true;
    clone.vssId = 'creator-enhanced';
    clone.downloadable = true;
    if (asr?.baseUrl) {
      clone.asrBaseUrl = asr.baseUrl;
    } else {
      delete clone.asrBaseUrl;
    }
    injected.push(clone);
  }

  if (injected.length === 0) return arr;

  // Before type-1 ASR Enhanced / Whisper so creator 斷句 mirrors manual-before-ASR.
  const insertIdx = arr.findIndex(
    (track) => track.whisper === true || (track.enhanced === true && !track.creatorEnhanced),
  );
  if (insertIdx === -1) return arr.concat(injected);
  return [...arr.slice(0, insertIdx), ...injected, ...arr.slice(insertIdx)];
}

export function injectYoutubeService(
  captionTracks: YoutubeCaptionTrack[],
  labels: YoutubeServiceLabels,
  options: { injectCreatorEnhanced?: boolean } = {},
): YoutubeCaptionTrack[] {
  let arr = captionTracks.slice();
  const obj: YoutubeCaptionTrack = {
    name: { simpleText: labels.whisperTitle },
    whisper: true,
    vssId: 'whisper',
    downloadable: true,
  };
  const restoredObj = JSON.parse(JSON.stringify(obj)) as YoutubeCaptionTrack;
  restoredObj.restored = true;
  restoredObj.name.simpleText += ` (${labels.restoredSuffix})`;

  // Creator 斷句 first (same order as manuals), then ASR 斷句/修復, then Whisper.
  if (options.injectCreatorEnhanced !== false) {
    arr = injectYoutubeCreatorEnhancedTracks(arr, labels);
  }

  const asrTrack = arr.find(
    (track) =>
      track.kind === 'asr'
      && !track.enhanced
      && !track.whisper
      && !track.restored
      && !track.creatorEnhanced,
  );

  if (asrTrack !== undefined) {
    const clone = JSON.parse(JSON.stringify(asrTrack)) as YoutubeCaptionTrack;
    clone.name.simpleText += ` (${labels.enhancedSuffix})`;
    clone.enhanced = true;
    clone.vssId = 'enhanced';
    clone.downloadable = true;

    const restoredClone = JSON.parse(JSON.stringify(clone)) as YoutubeCaptionTrack;
    restoredClone.name.simpleText += ` (${labels.restoredSuffix})`;
    restoredClone.restored = true;

    // Tail after natives: creator-* (already in) → ASR Enhanced → Restored → Whisper.
    arr.push(clone);
    arr.push(restoredClone);
  }

  arr.push(obj);
  arr.push(restoredObj);
  return arr;
}

export function selectionConverterCaptionTracks(
  captionTracks: YoutubeCaptionTrack[],
): TrackList['subtitles'] {
  return captionTracks.map((track, index) => ({
    name: track.name.simpleText,
    value: index,
    code: track.languageCode ?? '',
    downloadable: track.downloadable,
  }));
}

/**
 * YouTube auto-pick (MRU language):
 * - Type 1 ASR Enhanced: sticky when no plain creator for that language
 *   (ASR + Enhanced rows are grouped at the end; first pass fills plain).
 * - Type 2 creator-enhanced: never sticky — prefer plain creator instead.
 * - Whisper / restored: skip.
 */
export function pickPreferredYoutubeTrackIndex(
  captionTracks: YoutubeCaptionTrack[],
  languages: LanguagePreferenceList,
): number | undefined {
  if (shouldSkipPreferredSelection(languages) || languages.length === 0) {
    return undefined;
  }

  let track: number | undefined;
  let picked = false;

  for (let i = 0; i < languages.length; i += 1) {
    const languageCode = languages[i];
    if (picked || languageCode === undefined) break;

    for (let j = 0; j < captionTracks.length; j += 1) {
      const entry = captionTracks[j];
      if (!entry) continue;
      if (entry.whisper || entry.restored) continue;
      // Type 2: never auto-select creator-enhanced — fall back to plain creator.
      if (entry.creatorEnhanced === true) continue;
      const trackLanguageCode = entry.languageCode;
      if (trackLanguageCode === undefined) continue;

      if (languageCode === trackLanguageCode) {
        if (entry.enhanced === true) {
          // Type 1 sticky: take Enhanced unless a plain creator already won.
          const cur = track !== undefined ? captionTracks[track] : undefined;
          if (track === undefined || cur?.kind === 'asr') {
            track = j;
            picked = true;
            break;
          }
          continue;
        }
        if (entry.kind !== 'asr') {
          track = j;
        } else if (track === undefined) {
          track = j;
        }
      }

      if (languageCode.includes(trackLanguageCode) || trackLanguageCode.includes(languageCode)) {
        if (entry.enhanced === true) {
          const cur = track !== undefined ? captionTracks[track] : undefined;
          if (track === undefined || cur?.kind === 'asr') {
            track = j;
            picked = true;
            break;
          }
          continue;
        }
        if (entry.kind !== 'asr' && track === undefined) {
          track = j;
        }
      }
    }

    if (track !== undefined) {
      picked = true;
    }
  }

  return track;
}

// --- Netflix (B-SRC-NF-01, B-SEL-01) ---

export type NetflixTextTrack = {
  rank?: number;
  trackVariant?: unknown;
  rawTrackType?: string;
  languageDescription?: string;
  language?: string;
  /** Injected Optimized / Enhanced clone (method 3). */
  enhanced?: boolean;
  /** Native track id to fetch when `enhanced` is set. */
  optimizeSourceId?: string;
  id?: string;
};

/**
 * Append Optimized clones at the end (one per selectable native track).
 * Same downloadables/id source via `optimizeSourceId`; menu shows Enhanced suffix.
 */
export function injectNetflixOptimizedTracks<T extends NetflixTextTrack>(
  textTracks: T[],
  enhancedSuffix: string,
): T[] {
  const arr = textTracks.slice();
  const injected: T[] = [];

  for (let i = 0; i < textTracks.length; i += 1) {
    const track = textTracks[i];
    if (!track || !isSelectableNetflixTextTrack(track) || track.enhanced) continue;

    const clone = JSON.parse(JSON.stringify(track)) as T;
    const baseName = track.languageDescription ?? track.language ?? 'Track';
    clone.languageDescription = `${baseName} (${enhancedSuffix})`;
    clone.enhanced = true;
    clone.optimizeSourceId = typeof track.id === 'string' ? track.id : undefined;
    if (typeof track.id === 'string') {
      clone.id = `${track.id}__opt`;
    }
    injected.push(clone);
  }

  return arr.concat(injected);
}

export function selectionConverterNetflixTextTracks(
  textTracks: NetflixTextTrack[],
): TrackList['subtitles'] {
  const arr: TrackList['subtitles'] = [];

  for (let i = 0; i < textTracks.length; i += 1) {
    const track = textTracks[i];
    if (!track || track.rank === -1 || track.trackVariant) continue;

    const isCC = track.rawTrackType === 'closedcaptions';
    arr.push({
      name: `${track.languageDescription ?? ''}${isCC && !track.enhanced ? ' (CC)' : ''}`,
      value: i,
      code: track.language ?? '',
    });
  }

  return arr;
}

export function isSelectableNetflixTextTrack(track: NetflixTextTrack): boolean {
  return track.rank !== -1 && !track.trackVariant;
}

export function pickPreferredNetflixTrackIndex(
  textTracks: NetflixTextTrack[],
  languages: LanguagePreferenceList,
): number | undefined {
  if (shouldSkipPreferredSelection(languages)) {
    return undefined;
  }

  let track: number | undefined;
  let picked = false;

  for (let i = 0; i < languages.length; i += 1) {
    if (picked) break;
    const languageCode = languages[i];
    if (languageCode === undefined) continue;

    for (let j = 0; j < textTracks.length; j += 1) {
      const entry = textTracks[j];
      if (!entry || !isSelectableNetflixTextTrack(entry)) continue;

      if (languageCode === entry.language) {
        // Type 3 Optimized: never sticky — prefer plain native track.
        if (entry.enhanced === true) continue;
        if (track === undefined) {
          track = j;
        }
      }
    }

    if (track !== undefined) {
      picked = true;
    }
  }

  return track;
}

// --- Coursera (B-SRC-CO-01, B-SEL-01) ---

export type CourseraSubtitleRow = TrackList['subtitles'][number];

export function subtitlesFromCourseraVtt(
  subtitlesVtt: Record<string, string>,
  translationLanguages: Record<string, string>,
): TrackList['subtitles'] {
  const subtitles: TrackList['subtitles'] = [];
  const entries = Object.entries(subtitlesVtt);
  for (let i = 0; i < entries.length; i += 1) {
    const [code] = entries[i] ?? [];
    if (code === undefined) continue;
    subtitles.push({
      name: translationLanguages[code] ?? code,
      value: i,
      code,
    });
  }
  return subtitles;
}

/**
 * Append Optimized clones at the end (one per native language).
 * `code` stays the native language for VTT fetch; `optimizeSourceCode` mirrors it.
 */
export function injectCourseraOptimizedTracks(
  subtitles: CourseraSubtitleRow[],
  enhancedSuffix: string,
): CourseraSubtitleRow[] {
  const native = subtitles
    .filter((row) => !row.enhanced)
    .map((row, index) => ({
      name: row.name,
      value: index,
      code: row.code,
    }));
  const injected: CourseraSubtitleRow[] = [];

  for (let i = 0; i < native.length; i += 1) {
    const row = native[i];
    if (!row) continue;
    injected.push({
      name: `${row.name} (${enhancedSuffix})`,
      value: native.length + injected.length,
      code: row.code,
      enhanced: true,
      optimizeSourceCode: row.code,
    });
  }

  return native.concat(injected);
}

export function pickPreferredCourseraTrackIndex(
  subtitles: TrackList['subtitles'],
  languages: LanguagePreferenceList,
): number | undefined {
  if (shouldSkipPreferredSelection(languages)) {
    return undefined;
  }

  let track: number | undefined;
  let picked = false;

  for (let i = 0; i < languages.length; i += 1) {
    if (picked) break;
    const languageCode = languages[i];
    if (languageCode === undefined) continue;

    for (let j = 0; j < subtitles.length; j += 1) {
      const row = subtitles[j];
      if (!row || languageCode !== row.code) continue;
      // Type 3 Optimized: never sticky — prefer plain native track.
      if (row.enhanced === true) continue;
      track = j;
      picked = true;
      break;
    }
  }

  return track;
}

// --- Prime Video (B-SRC-PV-01, B-SEL-01) ---

export type PrimeSubtitleUrl = {
  displayName: string;
  languageCode: string;
  url?: string;
  trackGroupId?: string;
  /** Injected Optimized / Enhanced clone (method 3). */
  enhanced?: boolean;
  /** Native index to fetch when `enhanced` is set. */
  optimizeSourceIndex?: number;
};

/**
 * Append Optimized clones at the end (one per native track).
 * Source URL via `optimizeSourceIndex`; menu shows Enhanced suffix.
 */
export function injectPrimeOptimizedTracks<T extends PrimeSubtitleUrl>(
  subtitleUrls: T[],
  enhancedSuffix: string,
): T[] {
  const native = subtitleUrls.filter((entry) => !entry.enhanced);
  const injected: T[] = [];

  for (let i = 0; i < native.length; i += 1) {
    const entry = native[i];
    if (!entry) continue;
    const clone = JSON.parse(JSON.stringify(entry)) as T;
    clone.displayName = `${entry.displayName} (${enhancedSuffix})`;
    clone.enhanced = true;
    clone.optimizeSourceIndex = i;
    // Avoid accidental direct fetch of a missing URL on the clone.
    delete clone.url;
    injected.push(clone);
  }

  return native.concat(injected);
}

export function selectionConverterPrimeSubtitleUrls(
  subtitleUrls: PrimeSubtitleUrl[],
): TrackList['subtitles'] {
  return subtitleUrls.map((entry, index) => ({
    name: entry.displayName,
    value: index,
    code: entry.languageCode,
    ...(entry.enhanced
      ? { enhanced: true, optimizeSourceCode: entry.languageCode }
      : {}),
  }));
}

export function pickPreferredPrimeTrackIndex(
  subtitleUrls: PrimeSubtitleUrl[],
  languages: LanguagePreferenceList,
): number | undefined {
  if (shouldSkipPreferredSelection(languages)) {
    return undefined;
  }

  let track: number | undefined;
  let picked = false;

  for (let i = 0; i < languages.length; i += 1) {
    if (picked) break;
    const languageCode = languages[i];
    if (languageCode === undefined) continue;

    for (let j = 0; j < subtitleUrls.length; j += 1) {
      const entry = subtitleUrls[j];
      if (!entry || languageCode !== entry.languageCode) continue;
      // Type 3 Optimized: never sticky — prefer plain native track.
      if (entry.enhanced === true) continue;
      track = j;
      picked = true;
      break;
    }
  }

  return track;
}
